import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Flame, Plus, Heart, Clock, Search,
  Star, Play, ChevronLeft, ChevronRight,
  Loader, Send, X, MessageCircle, Sparkles,
  Film, Tv, Music, Globe, Tag, Zap,
  Eye, Award, Crown, Check
} from 'lucide-react';
import { peakMomentsAPI } from '../services/api';
import CreateThreadModal from './CreateThreadModal';
import PeakMomentScenes from './PeakMomentScenes';
import PeakMomentsFeed from './PeakMomentsFeed';

// Modern color palette
const colors = {
  black: '#000000',
  dark: '#0a0a0a',
  white: '#ffffff',
  gray: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1f1f23',
    900: '#111114',
    950: '#09090b',
  },
  purple: {
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
  },
  pink: {
    400: '#F472B6',
    500: '#EC4899',
    600: '#DB2777',
  },
  green: {
    400: '#4ADE80',
    500: '#22C55E',
  },
  yellow: {
    400: '#FACC15',
    500: '#EAB308',
  },
  red: {
    400: '#F87171',
    500: '#EF4444',
  },
  orange: {
    400: '#FB923C',
    500: '#F97316',
    600: '#EA580C',
  },
  cyan: {
    400: '#22D3EE',
    500: '#06B6D4',
  }
};

// Categories (secondary filter)
const CATEGORIES = {
  'all': { icon: Zap, label: 'All', gradient: 'linear-gradient(135deg, #F97316, #EAB308)' },
  'movies': { icon: Film, color: colors.purple[500], label: 'Movies', gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' },
  'tv-shows': { icon: Tv, color: colors.green[500], label: 'TV Shows', gradient: 'linear-gradient(135deg, #22C55E, #4ADE80)' },
  'music-performance': { icon: Music, color: colors.yellow[500], label: 'Music', gradient: 'linear-gradient(135deg, #EAB308, #FACC15)' },
  'anime': { icon: Sparkles, color: colors.pink[500], label: 'Anime', gradient: 'linear-gradient(135deg, #EC4899, #F472B6)' },
  'korean-drama': { icon: Globe, color: colors.cyan[500], label: 'K-Drama', gradient: 'linear-gradient(135deg, #06B6D4, #22D3EE)' },
};

// Moment Types (PRIMARY filter)
const MOMENT_TYPES = {
  'all': { label: 'All Moments', emoji: '✨', color: colors.orange[500], gradient: 'linear-gradient(135deg, #F97316, #EAB308)' },
  'epic': { label: 'Epic', emoji: '🔥', color: colors.orange[500], gradient: 'linear-gradient(135deg, #F97316, #FB923C)' },
  'viral': { label: 'Viral', emoji: '📈', color: colors.red[500], gradient: 'linear-gradient(135deg, #EF4444, #F87171)' },
  'plot_twist': { label: 'Plot Twist', emoji: '🤯', color: colors.yellow[500], gradient: 'linear-gradient(135deg, #EAB308, #FACC15)' },
  'emotional': { label: 'Emotional', emoji: '😭', color: colors.pink[500], gradient: 'linear-gradient(135deg, #EC4899, #F472B6)' },
  'funny': { label: 'Funny', emoji: '😂', color: colors.green[500], gradient: 'linear-gradient(135deg, #22C55E, #4ADE80)' },
  'shocking': { label: 'Shocking', emoji: '😱', color: colors.red[400], gradient: 'linear-gradient(135deg, #F87171, #FCA5A5)' },
  'wholesome': { label: 'Wholesome', emoji: '🥰', color: colors.green[400], gradient: 'linear-gradient(135deg, #4ADE80, #86EFAC)' },
  'dramatic': { label: 'Dramatic', emoji: '🎭', color: colors.purple[500], gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' },
};

// Loading spinner
const LoadingSpinner = ({ size = 20 }) => (
  <Loader style={{ width: size, height: size, animation: 'spin 1s linear infinite' }} />
);

// Toast notification
const Toast = ({ type, children, onClose }) => {
  const styles = {
    success: { bg: 'rgba(34, 197, 94, 0.15)', border: colors.green[500], icon: '✓' },
    error: { bg: 'rgba(239, 68, 68, 0.15)', border: colors.red[500], icon: '✕' },
    info: { bg: 'rgba(139, 92, 246, 0.15)', border: colors.purple[500], icon: 'ℹ' }
  };
  const style = styles[type] || styles.info;

  return (
    <div style={{
      position: 'fixed',
      top: '24px',
      right: '24px',
      padding: '16px 24px',
      backgroundColor: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: '12px',
      color: colors.white,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      zIndex: 1001,
      animation: 'slideInRight 0.3s ease-out'
    }}>
      <span style={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: style.border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>{style.icon}</span>
      <span style={{ fontSize: '14px', fontWeight: '500' }}>{children}</span>
      {onClose && (
        <button onClick={onClose} style={{
          background: 'none',
          border: 'none',
          color: colors.gray[400],
          cursor: 'pointer',
          padding: '4px',
          marginLeft: '8px'
        }}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const VideoEmbedModal = ({ moment, onClose }) => {
  const embedOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const embedParams = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    origin: embedOrigin,
    start: String(Math.max(0, Number(moment?.timestampSeconds) || 0))
  }).toString();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!moment?.videoId) return null;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.88)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.12)', border: 'none',
          borderRadius: '50%', width: '36px', height: '36px',
          color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}
      >
        <X size={18} />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative', width: '90vw', maxWidth: '960px',
          aspectRatio: '16/9', background: '#000',
          borderRadius: '12px', overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)'
        }}
      >
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${moment.videoId}?${embedParams}`}
          title={moment.videoTitle || moment.title || 'Peak moment video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </div>
    </div>,
    document.body
  );
};

// LARGE Featured Card - for spotlight positions
// size: 'large' | 'medium' | 'small'
const FeaturedCard = ({ moment, onLike, onStartThread, onWatch, rank, size = 'large' }) => {
  const [liked, setLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const momentTypeConfig = MOMENT_TYPES[moment.momentType] || MOMENT_TYPES.epic;
  const isMobile = window.innerWidth < 768;

  const sz = {
    large:  { badge: 48, badgeFont: 20, play: 80, playIcon: 36, typePad: '8px 16px', typeFont: 14, typeEmoji: 18, titleFont: 15, subFont: 13, metaFont: 12, pad: 16 },
    medium: { badge: 36, badgeFont: 15, play: 56, playIcon: 24, typePad: '6px 12px', typeFont: 12, typeEmoji: 15, titleFont: 13, subFont: 12, metaFont: 11, pad: 12 },
    small:  { badge: 28, badgeFont: 12, play: 42, playIcon: 18, typePad: '4px 9px',  typeFont: 11, typeEmoji: 13, titleFont: 12, subFont: 11, metaFont: 10, pad: 10 },
  }[size];

  const rankBg = rank === 1
    ? 'linear-gradient(135deg, #FFD700, #FFA500)'
    : rank === 2
    ? 'linear-gradient(135deg, #C0C0C0, #A0A0A0)'
    : rank === 3
    ? 'linear-gradient(135deg, #CD7F32, #8B4513)'
    : 'rgba(0,0,0,0.75)';

  const handleLike = async (e) => {
    e.stopPropagation();
    try {
      const momentId = moment._id || moment.id;
      if (!momentId) return;
      await onLike(momentId);
      setLiked(!liked);
    } catch (error) {
      console.error('Failed to like:', error);
    }
  };

  const handleWatch = () => onWatch(moment);

  if (isMobile) {
    return (
      <div style={{
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: colors.gray[900],
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        cursor: 'pointer'
      }}>
        {/* Image */}
        <div onClick={handleWatch} style={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
          <img
            src={moment.videoThumbnail || '/api/placeholder/320/180'}
            alt={moment.videoTitle}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* Rank + type badges */}
          {rank && (
            <div style={{
              position: 'absolute', top: '10px', left: '10px',
              width: 36, height: 36, background: rankBg, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)', zIndex: 2
            }}>
              <span style={{ fontSize: 14, fontWeight: '800', color: colors.white }}>#{rank}</span>
            </div>
          )}
          <div style={{
            position: 'absolute', top: '10px', right: '10px',
            padding: '4px 10px', background: momentTypeConfig.gradient,
            borderRadius: '20px', fontSize: 11, fontWeight: '700',
            color: colors.white, display: 'flex', alignItems: 'center', gap: '4px', zIndex: 2
          }}>
            <span>{momentTypeConfig.emoji}</span>
            <span>{momentTypeConfig.label}</span>
          </div>
          {/* Timestamp */}
          <div style={{
            position: 'absolute', bottom: '8px', right: '8px',
            padding: '3px 8px', backgroundColor: 'rgba(0,0,0,0.85)',
            borderRadius: '6px', fontSize: 11, color: colors.orange[400],
            fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px', zIndex: 2
          }}>
            <Clock size={11} />{moment.timestampFormatted}
          </div>
        </div>
        {/* Text section */}
        <div style={{ padding: '10px 12px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
            <img
              src={moment.channelAvatarUrl || '/api/placeholder/28/28'}
              alt={moment.channelName}
              style={{ width: 24, height: 24, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.2)', flexShrink: 0 }}
            />
            <span style={{ fontSize: 12, color: colors.gray[300], fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {moment.channelName}
            </span>
          </div>
          <h3 style={{
            fontSize: 13, fontWeight: '700', color: colors.white,
            margin: '0 0 4px 0', lineHeight: '1.35',
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {moment.videoTitle}
          </h3>
          {moment.title && (
            <p style={{ fontSize: 11, color: colors.orange[300], fontStyle: 'italic', margin: '0 0 4px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              "{moment.title}"
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <span style={{ fontSize: 10, color: colors.gray[500] }}>
              Peaked by <span style={{ color: colors.purple[400] }}>@{moment.submittedBy || 'community'}</span>
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={handleLike} style={{ padding: '5px 10px', background: liked ? colors.pink[500] : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '8px', color: colors.white, cursor: 'pointer', fontSize: 11, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Heart size={12} fill={liked ? 'white' : 'none'} />{moment.likes || 0}
              </button>
              <button onClick={(e) => { e.stopPropagation(); onStartThread({ type: 'peak_moment', id: moment.id || moment._id, title: moment.title, thumbnail: moment.videoThumbnail, peakMomentData: { videoId: moment.videoId, timestamp: moment.timestampSeconds, channelName: moment.channelName } }); }} style={{ padding: '5px 9px', background: 'rgba(139,92,246,0.15)', border: 'none', borderRadius: '8px', color: colors.purple[400], cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <MessageCircle size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleWatch}
      style={{
        borderRadius: size === 'large' ? '20px' : '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.4s ease, box-shadow 0.4s ease',
        transform: isHovered ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isHovered
          ? `0 24px 48px rgba(0,0,0,0.5), 0 0 0 2px ${momentTypeConfig.color}60`
          : '0 8px 32px rgba(0,0,0,0.3)',
        backgroundColor: colors.gray[900],
      }}
    >
      {/* Every tile keeps the thumbnail's native 16:9. Stretching it to fill an
          arbitrary grid cell is what used to crop faces out of the frame. */}
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        flexShrink: 0,
        overflow: 'hidden',
        backgroundColor: colors.gray[800],
      }}>
        <img
          src={moment.videoThumbnail || '/api/placeholder/400/225'}
          alt={moment.videoTitle}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 0.6s ease',
            transform: isHovered ? 'scale(1.08)' : 'scale(1)'
          }}
        />

        {/* Rank Badge */}
        {rank && (
          <div style={{
            position: 'absolute', top: sz.pad, left: sz.pad,
            width: sz.badge, height: sz.badge,
            background: rankBg, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 2
          }}>
            <span style={{ fontSize: sz.badgeFont, fontWeight: '800', color: colors.white }}>#{rank}</span>
          </div>
        )}

        {/* Type Badge */}
        <div style={{
          position: 'absolute', top: sz.pad, right: sz.pad,
          padding: sz.typePad, background: momentTypeConfig.gradient,
          borderRadius: '24px', fontSize: sz.typeFont, fontWeight: '700',
          color: colors.white, display: 'flex', alignItems: 'center', gap: '5px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 2
        }}>
          <span style={{ fontSize: sz.typeEmoji }}>{momentTypeConfig.emoji}</span>
          {size !== 'small' && <span>{momentTypeConfig.label}</span>}
        </div>

        {/* Play Button */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: isHovered ? 1 : 0.75,
          transition: 'all 0.3s ease',
          zIndex: 2
        }}>
          <div style={{
            width: sz.play, height: sz.play,
            background: 'linear-gradient(135deg, #F97316, #EA580C)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(249, 115, 22, 0.5)',
            transform: isHovered ? 'scale(1.12)' : 'scale(1)',
            transition: 'transform 0.3s ease'
          }}>
            <Play size={sz.playIcon} fill="white" style={{ color: 'white', marginLeft: '3px' }} />
          </div>
        </div>

        {/* Timestamp */}
        <div style={{
          position: 'absolute', bottom: sz.pad, right: sz.pad,
          padding: '3px 7px', background: 'rgba(0,0,0,0.78)', borderRadius: '6px',
          fontSize: sz.metaFont, color: colors.orange[400], fontWeight: '700',
          display: 'flex', alignItems: 'center', gap: '3px', zIndex: 2
        }}>
          <Clock size={sz.metaFont - 1} />
          {moment.timestampFormatted}
        </div>
      </div>

      {/* Text section — grows so cards sharing a row end flush with each other */}
      <div style={{
        flex: 1,
        padding: sz.pad,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px'
      }}>
        {/* Channel row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img
            src={moment.channelAvatarUrl || '/api/placeholder/40/40'}
            alt={moment.channelName}
            style={{ width: sz.pad + 8, height: sz.pad + 8, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', flexShrink: 0 }}
          />
          <span style={{
            fontSize: sz.metaFont, color: colors.gray[300], fontWeight: '600',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {moment.channelName}
          </span>
        </div>

        {/* Video title */}
        <h3 style={{
          fontSize: sz.titleFont, fontWeight: '700', color: colors.white,
          margin: 0, lineHeight: '1.3',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>
          {moment.videoTitle}
        </h3>

        {/* Community comment */}
        {moment.title && size !== 'small' && (
          <p style={{
            margin: 0, fontSize: sz.subFont, color: colors.orange[300], fontStyle: 'italic',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            "{moment.title}"
          </p>
        )}

        {/* Peaked by */}
        {moment.submittedBy && (
          <p style={{ fontSize: sz.metaFont, color: colors.gray[500], margin: 0 }}>
            Peaked by <span style={{ color: colors.purple[400], fontWeight: '600' }}>@{moment.submittedBy}</span>
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '6px', paddingTop: '2px' }}>
          <button
            onClick={handleLike}
            style={{
              padding: size === 'large' ? '6px 12px' : '5px 10px',
              background: liked ? colors.pink[500] : 'rgba(255,255,255,0.07)',
              border: 'none', borderRadius: '8px', color: colors.white,
              cursor: 'pointer', fontSize: sz.metaFont, fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s'
            }}
          >
            <Heart size={sz.metaFont} fill={liked ? 'white' : 'none'} />
            {moment.likes || 0}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStartThread({
                type: 'peak_moment', id: moment.id || moment._id, title: moment.title,
                thumbnail: moment.videoThumbnail,
                peakMomentData: { videoId: moment.videoId, timestamp: moment.timestampSeconds, channelName: moment.channelName }
              });
            }}
            style={{
              padding: size === 'large' ? '6px 10px' : '5px 9px',
              background: 'rgba(139,92,246,0.12)', border: 'none', borderRadius: '8px',
              color: colors.purple[400], cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s'
            }}
          >
            <MessageCircle size={sz.metaFont} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Clip length bounds — must match the server's clamp in routes/peakMoments.js
const CLIP_MIN = 5;
const CLIP_MAX = 60;
const CLIP_DEFAULT = 15;

// Submit Modal
const SubmitMomentModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    youtubeUrl: '',
    timestampInput: '',
    timestampSeconds: '',
    title: '',
    description: '',
    category: 'general',
    momentType: 'epic',
    clipDurationSeconds: CLIP_DEFAULT,
    isSpoiler: false
  });
  const [submitting, setSubmitting] = useState(false);

  const handleTimestampChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    let formatted = '';
    if (digitsOnly.length <= 2) {
      formatted = digitsOnly;
    } else if (digitsOnly.length <= 4) {
      formatted = digitsOnly.slice(0, 2) + ':' + digitsOnly.slice(2);
    } else {
      formatted = digitsOnly.slice(0, 2) + ':' + digitsOnly.slice(2, 4) + ':' + digitsOnly.slice(4, 6);
    }
    setFormData(prev => ({
      ...prev,
      timestampInput: formatted,
      timestampSeconds: timestampToSeconds(formatted)
    }));
  };

  const handleUrlChange = (url) => {
    setFormData(prev => ({ ...prev, youtubeUrl: url }));
    const tMatch = url.match(/[?&]t=(\d+)/);
    if (tMatch) {
      const seconds = parseInt(tMatch[1]);
      const formatted = secondsToTimestamp(seconds);
      setFormData(prev => ({
        ...prev,
        timestampInput: formatted,
        timestampSeconds: seconds
      }));
    }
  };

  const timestampToSeconds = (timestamp) => {
    const parts = timestamp.split(':').map(Number);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return 0;
  };

  const secondsToTimestamp = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.youtubeUrl || !formData.timestampSeconds) {
      alert('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        timestampSeconds: parseInt(formData.timestampSeconds)
      });
      setFormData({
        youtubeUrl: '',
        timestampInput: '',
        timestampSeconds: '',
        title: '',
        description: '',
        category: 'general',
        momentType: 'epic',
        clipDurationSeconds: CLIP_DEFAULT,
        isSpoiler: false
      });
      onClose();
    } catch (error) {
      alert('Failed to submit: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.03)',
    color: colors.white,
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(8px)'
    }}>
      <div style={{
        backgroundColor: colors.gray[900],
        padding: '36px',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.1)',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 24px 48px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #F97316, #EAB308)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Flame size={26} style={{ color: colors.white }} />
            </div>
            <div>
              <h2 style={{ color: colors.white, fontSize: '22px', fontWeight: '700', margin: 0 }}>
                Submit Peak Moment
              </h2>
              <p style={{ color: colors.gray[500], fontSize: '13px', margin: '4px 0 0 0' }}>
                Share an amazing reaction moment
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.05)',
            border: 'none',
            color: colors.gray[400],
            cursor: 'pointer',
            padding: '10px',
            borderRadius: '10px'
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* YouTube URL */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
              YouTube Video URL
            </label>
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = colors.orange[500]}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          {/* Timestamp + Category row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
                Timestamp
              </label>
              <input
                type="text"
                value={formData.timestampInput}
                onChange={(e) => handleTimestampChange(e.target.value)}
                placeholder="01:23"
                style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '2px', textAlign: 'center' }}
                maxLength={8}
                onFocus={(e) => e.target.style.borderColor = colors.orange[500]}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
                Genre
              </label>
              {/* The option list is drawn by the OS, not the page: a translucent
                  background falls back to white and the inherited white text
                  disappears. Control and options both need solid colours. */}
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer', backgroundColor: colors.gray[800], colorScheme: 'dark' }}
              >
                {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, config]) => (
                  <option key={key} value={key} style={{ backgroundColor: colors.gray[800], color: colors.white }}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clip length — where the moment stops. The feed plays exactly this window. */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
              <label style={{ color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
                Clip Length
              </label>
              <span style={{ color: colors.orange[500], fontSize: '13px', fontWeight: '700', fontFamily: 'monospace' }}>
                {formData.clipDurationSeconds}s
              </span>
            </div>
            <input
              type="range"
              min={CLIP_MIN}
              max={CLIP_MAX}
              step={1}
              value={formData.clipDurationSeconds}
              onChange={(e) => setFormData(prev => ({ ...prev, clipDurationSeconds: parseInt(e.target.value, 10) }))}
              style={{ width: '100%', accentColor: colors.orange[500], cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: colors.gray[500], fontSize: '12px' }}>
              <span>{CLIP_MIN}s</span>
              <span style={{ fontFamily: 'monospace', color: colors.gray[400] }}>
                {formData.timestampSeconds
                  ? `Plays ${secondsToTimestamp(parseInt(formData.timestampSeconds))} → ${secondsToTimestamp(parseInt(formData.timestampSeconds) + formData.clipDurationSeconds)}`
                  : 'Set a timestamp to preview the range'}
              </span>
              <span>{CLIP_MAX}s</span>
            </div>
          </div>

          {/* Title */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
              Moment Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Describe the peak moment..."
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = colors.orange[500]}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          {/* Moment Type */}
          <div>
            <label style={{ display: 'block', marginBottom: '12px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
              Moment Type
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(MOMENT_TYPES).filter(([k]) => k !== 'all').slice(0, 8).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, momentType: key }))}
                  style={{
                    padding: '8px 16px',
                    border: formData.momentType === key ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '20px',
                    background: formData.momentType === key ? config.gradient : 'transparent',
                    color: colors.white,
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{config.emoji}</span>
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Spoiler flag — shows a warning tag while the clip plays in the feed */}
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, isSpoiler: !prev.isSpoiler }))}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
              padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
              borderRadius: '12px',
              border: `1px solid ${formData.isSpoiler ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'}`,
              background: formData.isSpoiler ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.03)',
              transition: 'all 0.2s'
            }}
          >
            <span style={{
              width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
              border: `2px solid ${formData.isSpoiler ? colors.red[500] : 'rgba(255,255,255,0.3)'}`,
              background: formData.isSpoiler ? colors.red[500] : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {formData.isSpoiler && <Check size={13} strokeWidth={3} color={colors.white} />}
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.white }}>
                ⚠️ Spoiler Alert
              </span>
              <span style={{ display: 'block', fontSize: '12px', color: colors.gray[400], marginTop: '2px' }}>
                Shows a spoiler warning tag while the clip plays in the feed
              </span>
            </span>
          </button>


          {/* Description */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add context about what makes this moment special..."
              style={{ ...inputStyle, minHeight: '100px', resize: 'none' }}
              onFocus={(e) => e.target.style.borderColor = colors.orange[500]}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          {/* Submit buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                color: colors.gray[400],
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.title || !formData.youtubeUrl || !formData.timestampSeconds}
              style={{
                flex: 2,
                padding: '14px',
                background: (!formData.title || !formData.youtubeUrl || !formData.timestampSeconds)
                  ? 'rgba(249, 115, 22, 0.3)'
                  : 'linear-gradient(135deg, #F97316, #EA580C)',
                border: 'none',
                borderRadius: '12px',
                color: colors.white,
                cursor: (!formData.title || !formData.youtubeUrl || !formData.timestampSeconds) ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (!formData.title || !formData.youtubeUrl || !formData.timestampSeconds) ? 0.5 : 1,
                boxShadow: (!formData.title || !formData.youtubeUrl || !formData.timestampSeconds)
                  ? 'none'
                  : '0 8px 24px rgba(249, 115, 22, 0.3)'
              }}
            >
              {submitting ? <LoadingSpinner size={18} /> : <Send size={18} />}
              {submitting ? 'Submitting...' : 'Submit Moment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const CommunityPeakMoments = () => {
  const [moments, setMoments] = useState([]);
  const [featuredMoments, setFeaturedMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('moments');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showCreateThreadModal, setShowCreateThreadModal] = useState(false);
  const [linkedContent, setLinkedContent] = useState(null);
  const [embedMoment, setEmbedMoment] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const isMobile = viewportWidth < 768;
  // { startId } while the feed is open, null when closed. Entry is an id, not an
  // index, so the same feed works for any scoped set of moments later on.
  const [feedRequest, setFeedRequest] = useState(null);
  const [likedIds, setLikedIds] = useState(() => new Set());
  const didAutoOpenFeed = useRef(false);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load moments
  const loadMoments = async () => {
    try {
      setLoading(true);
      const response = await peakMomentsAPI.getPeakMoments({
        page: 1,
        limit: 100
      });
      setMoments(response.data);
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to load peak moments' });
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedMoments = async () => {
    try {
      const response = await peakMomentsAPI.getFeaturedPeakMoments(10);
      setFeaturedMoments(response.data);
    } catch (error) {
      console.error('Failed to load featured moments:', error);
    }
  };

  useEffect(() => {
    loadMoments();
    loadFeaturedMoments();
  }, []);

  // Handle like
  const handleLike = async (momentId) => {
    try {
      await peakMomentsAPI.likePeakMoment(momentId);
      setMoments(prev => prev.map(moment => {
        const currentId = moment._id || moment.id;
        return currentId === momentId
          ? { ...moment, likes: (moment.likes || 0) + 1 }
          : moment;
      }));
      setFeaturedMoments(prev => prev.map(moment => {
        const currentId = moment._id || moment.id;
        return currentId === momentId
          ? { ...moment, likes: (moment.likes || 0) + 1 }
          : moment;
      }));
    } catch (error) {
      throw error;
    }
  };

  // Handle submit
  const handleSubmit = async (formData) => {
    await peakMomentsAPI.submitPeakMoment(formData);
    const contributions = parseInt(localStorage.getItem('peakMomentsContributions') || '0') + 1;
    localStorage.setItem('peakMomentsContributions', contributions.toString());
    setToast({
      type: 'success',
      message: 'Peak moment submitted! It will appear after admin approval.'
    });
    setTimeout(() => setToast(null), 5000);
  };

  // Handle start thread
  const handleStartThread = (contentInfo) => {
    setLinkedContent(contentInfo);
    setShowCreateThreadModal(true);
  };

  // Opens the single full-length video (used from inside the feed)
  const openFullVideo = (moment) => {
    if (moment?.videoId) {
      setEmbedMoment(moment);
      return;
    }
    const url = moment?.videoUrl || moment?.youtubeUrlWithTimestamp || moment?.youtubeUrl;
    if (url) window.open(url, '_blank');
  };

  // Cards now enter the feed at the clicked moment instead of dead-ending in a
  // one-off modal, so browsing rolls straight into continuous playback.
  const handleWatchMoment = (moment) => {
    if (!moment?.videoId) { openFullVideo(moment); return; }
    setFeedRequest({ startId: moment._id || moment.id });
  };

  // Filter moments
  const filteredMoments = useMemo(() => {
    return moments.filter(moment => {
      const matchesSearch = searchTerm === '' ||
        moment.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment.channelName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || moment.category === selectedCategory;
      const matchesType = selectedType === 'all' || moment.momentType === selectedType;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [moments, searchTerm, selectedCategory, selectedType]);

  const playableMoments = useMemo(
    () => filteredMoments.filter(m => m.videoId),
    [filteredMoments]
  );

  // Mobile is feed-first: open it automatically on the first visit of a session.
  // Session-scoped and ref-guarded, otherwise closing the feed would instantly
  // reopen it and trap the user.
  useEffect(() => {
    if (!isMobile || didAutoOpenFeed.current || playableMoments.length === 0) return;
    try {
      if (sessionStorage.getItem('pm_feed_autoopened')) { didAutoOpenFeed.current = true; return; }
      sessionStorage.setItem('pm_feed_autoopened', '1');
    } catch { /* private mode */ }
    didAutoOpenFeed.current = true;
    setFeedRequest({ startId: null });
  }, [isMobile, playableMoments]);

  /**
   * Ranked mosaic.
   *
   * Every moment lives in one grid ordered by likes, and tiles shrink as the
   * ranking descends: the top 2 take half the width each, the next 3 a third,
   * everything after a quarter. Tiles sharing a row are always the same width,
   * so rows line up exactly and each thumbnail can keep its native 16:9 —
   * the old bento forced #1 into a tall cell and cropped the frame to pieces.
   */
  const mosaic = useMemo(() => {
    // Admin drag-order wins where it has been set (priority defaults to 0, so an
    // untouched library still falls through to pure likes ranking).
    const ranked = [...filteredMoments].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0) || (b.likes || 0) - (a.likes || 0)
    );
    return ranked.map((moment, i) => {
      const tier = i < 2 ? 0 : i < 5 ? 1 : 2;
      return { moment, rank: i + 1, tier, size: ['large', 'medium', 'small'][tier] };
    });
  }, [filteredMoments]);

  // Column counts are chosen so each tier divides the row evenly (2 large, then
  // 3 medium, then quarters), which is what keeps every row flush.
  const mosaicColumns = isMobile ? 1 : viewportWidth < 1180 ? 6 : 12;
  const tierSpans = mosaicColumns === 12 ? [6, 4, 3] : mosaicColumns === 6 ? [3, 2, 2] : [1, 1, 1];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.dark,
      color: colors.white
    }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        /* Play Moments launcher — kept identical to the sidebar button */
        @keyframes pf-flow { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
        @keyframes pf-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.5), 0 6px 18px rgba(236,72,153,0.32); }
          50%      { box-shadow: 0 0 0 7px rgba(249,115,22,0), 0 8px 24px rgba(236,72,153,0.5); }
        }
        @keyframes pf-eq { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        .pf-btn { transition: transform 0.16s ease; }
        .pf-btn:hover { transform: translateY(-2px) scale(1.02); }
        ::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>

      {/* Toast */}
      {toast && (
        <Toast type={toast.type} onClose={() => setToast(null)}>
          {toast.message}
        </Toast>
      )}

      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: isMobile ? '16px 12px' : '32px 40px' }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          marginBottom: isMobile ? '16px' : '24px',
          gap: isMobile ? '12px' : '24px'
        }}>
          {/* Left - Title, with Submit sitting right alongside it */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <div style={{
                width: isMobile ? '40px' : '52px',
                height: isMobile ? '40px' : '52px',
                background: 'linear-gradient(135deg, #F97316, #EAB308)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Flame size={isMobile ? 22 : 28} style={{ color: colors.white }} />
              </div>
              <h1 style={{
                fontSize: isMobile ? '24px' : '36px',
                fontWeight: '800',
                color: colors.white,
                margin: 0
              }}>
                Peak Moments
              </h1>
              <button
                onClick={() => setShowSubmitModal(true)}
                style={{
                  padding: isMobile ? '11px 20px' : '14px 26px',
                  background: 'transparent',
                  border: '1.5px solid rgba(249,115,22,0.5)',
                  borderRadius: '14px',
                  color: colors.orange[400],
                  cursor: 'pointer',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(249,115,22,0.14)';
                  e.currentTarget.style.borderColor = 'rgba(249,115,22,0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(249,115,22,0.5)';
                }}
              >
                <Plus size={isMobile ? 17 : 20} />
                Submit Moment
              </button>
            </div>
            <p style={{
              fontSize: '14px',
              color: colors.gray[400],
              margin: 0
            }}>
              Community-curated highlights and scene-based creator reactions
            </p>
          </div>

        </div>

        {/* The feed is the headline feature, so it gets its own centred row
            rather than competing with the toolbar. Same flowing gradient,
            glow pulse and equaliser as the sidebar launcher. */}
        {playableMoments.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: isMobile ? '20px' : '30px'
          }}>
            <button
              type="button"
              className="pf-btn"
              onClick={() => setFeedRequest({ startId: null })}
              title="Play Peak Moments feed"
              style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center',
                padding: isMobile ? '12px 22px 12px 12px' : '13px 32px 13px 13px',
                borderRadius: '999px', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(110deg, #f97316, #ec4899, #8b5cf6, #f97316)',
                backgroundSize: '300% 100%',
                animation: 'pf-flow 7s linear infinite, pf-glow 2.8s ease-in-out infinite',
                color: '#fff', fontFamily: "'Manrope', sans-serif",
                whiteSpace: 'nowrap'
              }}
            >
              <span style={{
                width: isMobile ? '38px' : '44px', height: isMobile ? '38px' : '44px',
                borderRadius: '50%', flexShrink: 0,
                background: 'rgba(255,255,255,0.24)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Play size={isMobile ? 17 : 20} fill="#fff" color="#fff" style={{ marginLeft: '2px' }} />
              </span>

              <span style={{ textAlign: 'left', lineHeight: '1.2' }}>
                <span style={{ display: 'block', fontSize: isMobile ? '16px' : '18px', fontWeight: 800 }}>
                  Play Moments
                </span>
                <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, opacity: 0.85 }}>
                  {playableMoments.length} moment{playableMoments.length !== 1 ? 's' : ''} · swipe through
                </span>
              </span>

              {/* little equaliser so the button reads as "something is playing" */}
              <span style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '18px', flexShrink: 0 }}>
                {[0, 0.25, 0.5].map((d, i) => (
                  <span key={i} style={{
                    width: '3.5px', height: '18px', borderRadius: '2px',
                    background: 'rgba(255,255,255,0.85)',
                    transformOrigin: 'bottom',
                    animation: `pf-eq 0.9s ease-in-out ${d}s infinite`
                  }} />
                ))}
              </span>
            </button>
          </div>
        )}

        {/* Toolbar — view switch, search, genre and submit on one line */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          marginBottom: '18px'
        }}>
          <div style={{
            display: 'inline-flex',
            padding: '4px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            {[
              { id: 'moments', label: 'Moments' },
              { id: 'scenes', label: 'Scenes' }
            ].map(option => (
              <button
                key={option.id}
                onClick={() => setViewMode(option.id)}
                style={{
                  padding: isMobile ? '7px 14px' : '8px 18px',
                  border: 'none',
                  borderRadius: '9px',
                  background: viewMode === option.id ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: viewMode === option.id ? colors.white : colors.gray[400],
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '700',
                  transition: 'all 0.2s'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, minWidth: isMobile ? '100%' : '12px' }}>
            {viewMode === 'moments' && (
              <div style={{ position: 'relative', maxWidth: isMobile ? 'none' : '280px' }}>
                <Search style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', color: colors.gray[500],
                  width: '16px', height: '16px'
                }} />
                <input
                  type="text"
                  placeholder="Search moments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px 9px 36px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    color: colors.white,
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>
            )}
          </div>

          {/* Genre was six pills on their own row; a select says the same thing */}
          {viewMode === 'moments' && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '9px 12px',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                backgroundColor: selectedCategory === 'all' ? colors.gray[800] : '#3a2410',
                color: selectedCategory === 'all' ? colors.gray[300] : colors.orange[400],
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                outline: 'none',
                colorScheme: 'dark'
              }}
            >
              {Object.entries(CATEGORIES).map(([key, config]) => (
                <option key={key} value={key} style={{ backgroundColor: colors.gray[800], color: colors.white }}>
                  {key === 'all' ? 'All genres' : config.label}
                </option>
              ))}
            </select>
          )}

        </div>

        {viewMode === 'scenes' ? (
          <PeakMomentScenes isMobile={isMobile} onToast={setToast} />
        ) : (
        <>
        {/* PRIMARY FILTER — moment type. The only pill row left; genre moved
            into the toolbar select above. */}
        <div style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '28px',
          overflowX: 'auto',
          paddingBottom: '4px',
          scrollbarWidth: 'none'
        }}>
          {Object.entries(MOMENT_TYPES).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setSelectedType(key)}
              style={{
                padding: isMobile ? '7px 12px' : '8px 16px',
                border: selectedType === key ? 'none' : '1px solid rgba(255,255,255,0.09)',
                borderRadius: '999px',
                background: selectedType === key ? config.gradient : 'rgba(255,255,255,0.03)',
                color: selectedType === key ? colors.white : colors.gray[300],
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: selectedType === key ? '700' : '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: isMobile ? '13px' : '14px' }}>{config.emoji}</span>
              {config.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '120px 20px',
            gap: '16px'
          }}>
            <LoadingSpinner size={40} />
            <p style={{ color: colors.gray[500], fontSize: '15px' }}>Loading peak moments...</p>
          </div>
        ) : filteredMoments.length > 0 ? (
          <>
            {/* WHEN "ALL MOMENTS" IS SELECTED - Show Top 3 Bento + Grid */}
            {selectedType === 'all' ? (
              <>
                {/* RANKED MOSAIC — every moment in one grid, tiles shrinking by rank */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <Crown size={24} style={{ color: colors.yellow[400] }} />
                    <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>
                      Ranked Moments
                    </h2>
                    <span style={{
                      padding: '4px 12px',
                      background: 'rgba(250, 204, 21, 0.15)',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: colors.yellow[400],
                      fontWeight: '600'
                    }}>
                      Most Liked
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: colors.gray[400],
                      fontWeight: '500'
                    }}>
                      {mosaic.length} moment{mosaic.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${mosaicColumns}, 1fr)`,
                    gap: isMobile ? '16px' : '20px',
                    alignItems: 'stretch'
                  }}>
                    {mosaic.map(({ moment, rank, tier, size }, idx) => (
                      <div
                        key={moment._id || moment.id}
                        style={{
                          gridColumn: `span ${tierSpans[tier]}`,
                          animation: `fadeIn 0.4s ease-out ${Math.min(idx, 11) * 0.04}s backwards`
                        }}
                      >
                        <FeaturedCard
                          moment={moment}
                          onLike={handleLike}
                          onStartThread={handleStartThread}
                          onWatch={handleWatchMoment}
                          rank={rank}
                          size={size}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* WHEN A SPECIFIC TYPE IS SELECTED - Show medium cards grid */
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <span style={{ fontSize: '28px' }}>{MOMENT_TYPES[selectedType]?.emoji}</span>
                  <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>
                    {MOMENT_TYPES[selectedType]?.label} Moments
                  </h2>
                  <span style={{
                    padding: '4px 12px',
                    background: MOMENT_TYPES[selectedType]?.gradient,
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: colors.white,
                    fontWeight: '600'
                  }}>
                    {filteredMoments.length} moment{filteredMoments.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Medium-sized cards in a 2 or 3 column grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
                  gap: '24px'
                }}>
                  {filteredMoments.map((moment, idx) => (
                    <div
                      key={moment._id || moment.id}
                      style={{ animation: `fadeIn 0.4s ease-out ${idx * 0.05}s backwards` }}
                    >
                      <FeaturedCard
                        moment={moment}
                        onLike={handleLike}
                        onStartThread={handleStartThread}
                        onWatch={handleWatchMoment}
                        size="medium"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '120px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'rgba(249, 115, 22, 0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '8px'
            }}>
              <Flame size={40} style={{ color: colors.orange[500], opacity: 0.5 }} />
            </div>
            <h3 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>No peak moments found</h3>
            <p style={{ color: colors.gray[500], fontSize: '15px', maxWidth: '400px' }}>
              {searchTerm ? 'Try adjusting your search terms' : 'Be the first to share an amazing reaction moment!'}
            </p>
            <button
              onClick={() => setShowSubmitModal(true)}
              style={{
                marginTop: '16px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #F97316, #EA580C)',
                border: 'none',
                borderRadius: '12px',
                color: colors.white,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus size={18} />
              Submit First Moment
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Modals */}
      <SubmitMomentModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSubmit={handleSubmit}
      />

      {embedMoment && (
        <VideoEmbedModal
          moment={embedMoment}
          onClose={() => setEmbedMoment(null)}
        />
      )}

      {feedRequest && playableMoments.length > 0 && (
        <PeakMomentsFeed
          moments={playableMoments}
          startId={feedRequest.startId}
          likedIds={likedIds}
          onClose={() => setFeedRequest(null)}
          onLike={async (moment) => {
            const id = moment._id || moment.id;
            if (likedIds.has(id)) return;
            setLikedIds(prev => new Set(prev).add(id));
            try {
              await handleLike(id);
            } catch {
              setLikedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            }
          }}
          onOpenFull={(moment) => {
            // Must be openFullVideo, not handleWatchMoment — the latter now
            // opens the feed, which would loop straight back here.
            setFeedRequest(null);
            openFullVideo(moment);
          }}
        />
      )}

      <CreateThreadModal
        isOpen={showCreateThreadModal}
        onClose={() => {
          setShowCreateThreadModal(false);
          setLinkedContent(null);
        }}
        linkedContent={linkedContent}
        onThreadCreated={() => {
          setShowCreateThreadModal(false);
          setLinkedContent(null);
          setToast({ type: 'success', message: 'Thread created successfully!' });
          setTimeout(() => setToast(null), 3000);
        }}
      />
    </div>
  );
};

export default CommunityPeakMoments;
